import api, {
    setAccessToken,
    clearClientAuthState,
    getTokenExpiresAt,
    hasClientAuthState,
    isUnauthorizedError,
} from "./axiosConfig";

const getCookie = (name: string): string | null => {
    const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[2]) : null;
};

export interface BookmarkDto {
    id: number;
    title: string;
    summary: string;
    source_url: string;
    published_at: string;
}

export interface SubscriptionStatusDto {
    active: boolean;
    nextBillingDate: string | null;
    telegramLinked: boolean;
    cancelAtPeriodEnd?: boolean;
    plan?: string | null;
    status?: string | null;
}

export interface TelegramUpdateRequest {
    linked: boolean;
}

export interface BookmarkLite {
    id: string;
}

export interface LoginResponse {
    id: number;
    email: string;
    token?: string;
    accessToken?: string;
    expiresAt?: number;
    expiresIn?: number;
}

export interface UserProfileDto {
    id: number;
    email: string;
    phone?: string | null;
    marketingEmailAgree?: boolean;
    marketingSmsAgree?: boolean;
    role?: string | null;
    googleConnected?: boolean;
    kakaoConnected?: boolean;
    naverConnected?: boolean;
    isActive?: boolean;
    subscriptionStatus?: SubscriptionStatusDto;
}

/**
 * Server-authenticated session states consumed by App/authSlice.
 * AUTH_CHECKING belongs to the Redux/UI layer while this API call is in flight.
 */
export type AuthSessionState = "AUTHENTICATED" | "ANONYMOUS" | "SESSION_EXPIRED";

export interface AuthSessionCheckResult {
    state: AuthSessionState;
    authenticated: boolean;
    user: UserProfileDto | null;
}

export interface UpdateAccountSettingsPayload {
    phone?: string;
    marketingEmailAgree: boolean;
    marketingSmsAgree: boolean;
}

export interface ChangeEmailRequestPayload {
    newEmail: string;
}

export interface ChangeEmailRequestResponse {
    success: boolean;
    message?: string;
}

export type SocialProvider = "google" | "kakao" | "naver";

export interface SocialLinkUrlResponse {
    url: string;
}

export interface SocialUnlinkResponse {
    success: boolean;
    message?: string;
}

function persistLoginMeta(data: LoginResponse) {
    const token = data.token ?? data.accessToken ?? null;
    const now = Date.now();

    const expiresAt =
        data.expiresAt
        ?? (typeof data.expiresIn === "number" ? now + data.expiresIn * 1000 : null)
        ?? (token ? getTokenExpiresAt(token) : null);

    if (token) {
        setAccessToken(token, expiresAt);
    } else {
        // A successful login contract without an access token is not a usable
        // authenticated browser state for PolicyIntel's Bearer-protected APIs.
        clearClientAuthState();
        throw new Error("Login response did not include an access token");
    }

    if (typeof data.id === "number") {
        localStorage.setItem("userId", String(data.id));
    } else {
        localStorage.removeItem("userId");
    }

    if (data.email) {
        localStorage.setItem("email", data.email);
    } else {
        localStorage.removeItem("email");
    }

    localStorage.setItem("loginAt", String(now));

    if (expiresAt) {
        localStorage.setItem("expiresAt", String(expiresAt));
    } else {
        localStorage.removeItem("expiresAt");
    }

    // This cache exists for fast paint only. It is never the server authority.
    localStorage.setItem(
        "user",
        JSON.stringify({
            id: data.id,
            email: data.email,
            token,
            expiresAt: expiresAt ?? undefined,
        })
    );
}

// =======================
// Auth & User
// =======================

export const login = async (credentials: { email: string; password: string }) => {
    const {data} = await api.post<LoginResponse>("/user/login", credentials);

    persistLoginMeta(data);

    const gid = getCookie("gid");
    if (gid) {
        try {
            await api.post("/user/exchange", null, {headers: {"X-GID": gid}});
        } catch {
            // Analytics/session exchange must never turn a successful login into failure.
        }
    }

    return data;
};

/**
 * Canonical server-side authentication authority.
 *
 * - axiosConfig first retries an expired access token once via HttpOnly refresh.
 * - 200 means AUTHENTICATED.
 * - final 401/419 means the browser must not keep rendering a logged-in UI.
 * - whether it is ANONYMOUS or SESSION_EXPIRED is derived from the client state
 *   that existed before the authoritative request began.
 */
export const checkAuthSession = async (): Promise<AuthSessionCheckResult> => {
    const lookedAuthenticatedBeforeCheck = hasClientAuthState();

    try {
        const {data} = await api.get<UserProfileDto>("/user/me");
        return {
            state: "AUTHENTICATED",
            authenticated: true,
            user: data,
        };
    } catch (error) {
        if (!isUnauthorizedError(error)) {
            throw error;
        }

        clearClientAuthState();
        return {
            state: lookedAuthenticatedBeforeCheck ? "SESSION_EXPIRED" : "ANONYMOUS",
            authenticated: false,
            user: null,
        };
    }
};

/**
 * Backward-compatible profile API. Existing sagas/components may keep using this
 * while App boot/session restoration migrates to checkAuthSession().
 */
export const getUserInfo = async (): Promise<UserProfileDto> => {
    const {data} = await api.get<UserProfileDto>("/user/me");
    return data;
};

export const getSubscriptionStatus = async (): Promise<SubscriptionStatusDto> => {
    const {data} = await api.get<SubscriptionStatusDto>("/user/subscription");
    return data;
};

export const updateTelegramSetting = async (linked: boolean): Promise<boolean> => {
    const {data} = await api.post("/user/telegram", {linked} as TelegramUpdateRequest);
    return data as boolean;
};

export const updatePassword = async (oldPassword: string, newPassword: string) => {
    await api.put("/user/password", {oldPassword, newPassword});
    return true;
};

export const logout = async () => {
    try {
        await api.post("/user/logout");
    } finally {
        clearClientAuthState();
    }

    try {
        const r = await api.post("/anon/session");
        (window as any).__anonAccess = r.data?.accessToken;
    } catch {
        // Anonymous-session bootstrap is best effort only.
    }
};

export const logoutLocalOnly = () => {
    clearClientAuthState();
};

// =======================
// Bookmarks
// =======================

const BM_PATH = "/bookmarks";

export async function getBookmarks(): Promise<BookmarkLite[]> {
    const {data} = await api.get<BookmarkDto[]>(BM_PATH);
    return (data ?? []).map((d) => ({id: String(d.id)}));
}

export async function addBookmark(item: string | { id: string }): Promise<void> {
    const id = typeof item === "string" ? item : item.id;
    await api.post(BM_PATH, {id});
}

export async function removeBookmark(id: string): Promise<void> {
    await api.delete(`${BM_PATH}/${encodeURIComponent(id)}`);
}

export const updateAccountSettings = async (
    payload: UpdateAccountSettingsPayload
): Promise<UserProfileDto> => {
    const {data} = await api.put<UserProfileDto>("/user/me", payload);
    return data;
};

export const deleteMyAccount = async () => {
    await api.delete("/user/me");
    clearClientAuthState();
    return true;
};

export const requestEmailChange = async (
    payload: ChangeEmailRequestPayload
): Promise<ChangeEmailRequestResponse> => {
    const {data} = await api.post<ChangeEmailRequestResponse>("/user/email/change-request", payload);
    return data;
};

export const getSocialLinkUrl = async (
    provider: SocialProvider
): Promise<SocialLinkUrlResponse> => {
    const {data} = await api.get<SocialLinkUrlResponse>(`/user/social/${provider}/link-url`);
    return data;
};

export const unlinkSocialAccount = async (
    provider: SocialProvider
): Promise<SocialUnlinkResponse> => {
    const {data} = await api.delete<SocialUnlinkResponse>(`/user/social/${provider}`);
    return data;
};
